import { Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Dialog } from '@opendoor/atoms/dialog';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import type { SavedSearchFieldsFragment } from '__generated__/athena/graphql';

const SAVE_SEARCH_NAME_MAX_LENGTH = 80;

interface UpdateSavedSearchDialogProps {
  open: boolean;
  submitting: boolean;
  activeSavedSearch?: SavedSearchFieldsFragment | null;
  errorMessage: string | null;
  onClose: () => void;
  onSaveAsNew: () => void;
  onUpdateActive: () => void;
}

export const UpdateSavedSearchDialog = ({
  open,
  submitting,
  activeSavedSearch,
  errorMessage,
  onClose,
  onSaveAsNew,
  onUpdateActive,
}: UpdateSavedSearchDialogProps) => (
  <Dialog.Root
    role="dialog"
    open={open}
    onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        onClose();
      }
    }}
    disablePointerDismissal={submitting}
  >
    <Dialog title="Update saved search?">
      <Dialog.Description className="text-body-md text-od-content-secondary">
        {`Your filters have changed. Apply the changes to “${activeSavedSearch?.name}”, or keep it and save these filters as a new search.`}
      </Dialog.Description>
      {errorMessage && (
        <Text as="p" role="alert" className="pt-3 text-body-sm text-od-content-error">
          {errorMessage}
        </Text>
      )}
      <Row className="justify-end gap-2 pt-6 max-md:flex-wrap">
        <Button
          variant="secondary"
          size="md"
          aria-label="Save as new search"
          analyticsName="save-search-as-new"
          onClick={onSaveAsNew}
          disabled={submitting}
        >
          Save as new
        </Button>
        <Button
          variant="primary"
          size="md"
          aria-label={`Update ${activeSavedSearch?.name}`}
          analyticsName="save-search-update-active"
          onClick={onUpdateActive}
          disabled={submitting}
          loading={submitting}
        >
          Update
        </Button>
      </Row>
    </Dialog>
  </Dialog.Root>
);

interface NameSavedSearchDialogProps {
  open: boolean;
  name: string;
  submitting: boolean;
  duplicateOf: SavedSearchFieldsFragment | null;
  errorMessage: string | null;
  canSubmit: boolean;
  submitLabel: string;
  inputRef: (node: HTMLInputElement | null) => void;
  nameInputId: string;
  onNameChange: (name: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}

export const NameSavedSearchDialog = ({
  open,
  name,
  submitting,
  duplicateOf,
  errorMessage,
  canSubmit,
  submitLabel,
  inputRef,
  nameInputId,
  onNameChange,
  onClose,
  onSubmit,
}: NameSavedSearchDialogProps) => (
  <Dialog.Root
    role="dialog"
    open={open}
    onOpenChange={(nextOpen) => {
      if (!nextOpen) {
        onClose();
      }
    }}
    disablePointerDismissal={submitting}
  >
    <Dialog title="Name your saved search">
      <Dialog.Description className="text-body-md text-od-content-secondary">
        Give your search a name so you can find it later.
      </Dialog.Description>
      <Col className="pt-4 pb-2">
        <Col className="gap-1.5">
          <Text as="label" htmlFor={nameInputId} className="text-label-sm text-od-content-primary">
            Search name
          </Text>
          <Input
            ref={inputRef}
            id={nameInputId}
            size="base"
            value={name}
            onChange={(event) => {
              onNameChange(event.target.value);
            }}
            maxLength={SAVE_SEARCH_NAME_MAX_LENGTH}
            analyticsName="save-search-name-input"
            placeholder="My saved search"
          />
        </Col>
        {duplicateOf && !errorMessage && (
          <Text as="p" role="alert" className="pt-2 text-body-sm text-od-content-secondary">
            {`You already have this search saved as “${duplicateOf.name}”.`}
          </Text>
        )}
        {errorMessage && (
          <Text as="p" role="alert" className="pt-2 text-body-sm text-od-content-error">
            {errorMessage}
          </Text>
        )}
      </Col>
      <Row className="justify-end gap-2 pt-6">
        <Button
          variant="secondary"
          size="md"
          aria-label="Cancel"
          analyticsName="save-search-cancel"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          aria-label={submitLabel}
          analyticsName="save-search-submit"
          onClick={onSubmit}
          disabled={!canSubmit}
          loading={submitting}
        >
          {submitLabel}
        </Button>
      </Row>
    </Dialog>
  </Dialog.Root>
);
